import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { RowActions, StatusBadge, emptyMessage, rowActionsTableStyle } from "../ui";
import { BenefitThumbnail } from "../ui/BenefitThumbnail";
import { endOfDay, format, startOfDay } from "date-fns";

import { useGetBenefitsQuery } from "../../store/services/benefits";
import { useDebounce } from "../../hooks/useDebounce";
import { Benefit, BenefitDiscountType, BenefitStatus } from "../../models/benefits";
import { PartnerBenefitFilters } from "../Filters/PartnerBenefitFilters";
import {
  getBenefitDiscountTypeLabel,
  getBenefitStatusLabel,
  getBenefitStatusTone,
} from "../../utils/constants/benefits-constants";

const parseBenefitDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatBenefitDate = (value: string | null): string | undefined => {
  const date = parseBenefitDate(value);
  return date ? format(date, "dd/MM/yyyy") : undefined;
};

/**
 * Filtro de vigencia (client-side): la API de /bo/benefits todavía no acepta
 * date_from/date_to, así que el rango se aplica sobre la lista ya cargada.
 * Mismo criterio que BenefitListDT.
 */
const isWithinVigenciaRange = (
  benefit: Benefit,
  vigenciaFrom: Date | null,
  vigenciaTo: Date | null
): boolean => {
  if (vigenciaFrom) {
    const start = parseBenefitDate(benefit.startDate);
    if (!start || start < startOfDay(vigenciaFrom)) return false;
  }
  if (vigenciaTo) {
    const end = parseBenefitDate(benefit.endDate);
    if (!end || end > endOfDay(vigenciaTo)) return false;
  }
  return true;
};

const getSortedImages = (benefit: Benefit) =>
  [...(benefit.images ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

interface PartnerBenefitsListDTProps {
  partnerId: string;
  tenantId?: string | null;
}

export const PartnerBenefitsListDT = ({
  partnerId,
  tenantId,
}: PartnerBenefitsListDTProps) => {
  const navigate = useNavigate();

  // Filtros: búsqueda por título, estado y tipo de descuento se aplican
  // client-side sobre el listado ya scopeado al partner (BenefitsParams no
  // soporta búsqueda de texto, date_from/date_to ni discount_type).
  const [title, setTitle] = useState<string>("");
  const [status, setStatus] = useState<BenefitStatus | undefined>();
  const [discountType, setDiscountType] = useState<
    BenefitDiscountType | undefined
  >();
  const [vigenciaFrom, setVigenciaFrom] = useState<Date | null>(null);
  const [vigenciaTo, setVigenciaTo] = useState<Date | null>(null);

  const clearAllFilters = () => {
    setTitle("");
    setStatus(undefined);
    setDiscountType(undefined);
    setVigenciaFrom(null);
    setVigenciaTo(null);
  };

  const debouncedTitle = useDebounce(title, 500);
  const effectiveTitle = title === "" ? "" : debouncedTitle;

  const { data, isLoading } = useGetBenefitsQuery(
    { partnerId, tenantId },
    { skip: !partnerId }
  );

  const filteredBenefits = useMemo(() => {
    const term = effectiveTitle.trim().toLowerCase();
    const list = data ?? [];
    return list.filter((benefit) => {
      if (term && !(benefit.title ?? "").toLowerCase().includes(term)) {
        return false;
      }
      if (status && benefit.status !== status) return false;
      if (discountType && benefit.discountType !== discountType) return false;
      return isWithinVigenciaRange(benefit, vigenciaFrom, vigenciaTo);
    });
  }, [data, effectiveTitle, status, discountType, vigenciaFrom, vigenciaTo]);

  // Column templates
  const imageBodyTemplate = (benefit: Benefit) => (
    <BenefitThumbnail
      url={getSortedImages(benefit)[0]?.imageUrl}
      alt={benefit.title ?? "Beneficio"}
      size={48}
    />
  );


  const titleBodyTemplate = (benefit: Benefit) => (
    <div className="flex flex-column">
      <span className="font-medium">{benefit.title ?? "-"}</span>
      {benefit.discountLabel && (
        <small className="text-color-secondary">{benefit.discountLabel}</small>
      )}
    </div>
  );

  const discountBodyTemplate = (benefit: Benefit) => {
    if (!benefit.discountType) return "-";
    const label = getBenefitDiscountTypeLabel(benefit.discountType);
    return (
      <Tag
        severity="info"
        value={
          benefit.discountValue ? `${label} · ${benefit.discountValue}` : label
        }
      />
    );
  };

  const validityBodyTemplate = (benefit: Benefit) => {
    const start = formatBenefitDate(benefit.startDate);
    const end = formatBenefitDate(benefit.endDate);
    if (start && end) return `${start} - ${end}`;
    if (start) return `Desde ${start}`;
    if (end) return `Hasta ${end}`;
    return "-";
  };

  const statusBodyTemplate = (benefit: Benefit) => (
    <StatusBadge
      label={getBenefitStatusLabel(benefit.status)}
      tone={getBenefitStatusTone(benefit.status)}
    />
  );

  // 2 acciones: iconos en linea (INC-03).
  const actionsBodyTemplate = (benefit: Benefit) => (
    <RowActions
      actions={[
        { icon: "pi pi-eye", label: "Ver", severity: "info", onClick: () => navigate(`/benefits-catalog/${benefit.id}`) },
        { icon: "pi pi-pencil", label: "Editar", onClick: () => navigate(`/benefits-catalog/${benefit.id}/edit`) },
      ]}
    />
  );

  return (
    <DataTable
      style={rowActionsTableStyle("1rem", "6.5rem")}
      value={filteredBenefits}
      loading={isLoading}
      paginator
      rows={10}
      rowsPerPageOptions={[5, 10, 25, 50]}
      emptyMessage={emptyMessage("beneficios")}
      header={
        <PartnerBenefitFilters
          title={title}
          setTitle={setTitle}
          status={status}
          setStatus={setStatus}
          discountType={discountType}
          setDiscountType={setDiscountType}
          vigenciaFrom={vigenciaFrom}
          setVigenciaFrom={setVigenciaFrom}
          vigenciaTo={vigenciaTo}
          setVigenciaTo={setVigenciaTo}
          onClearAll={clearAllFilters}
        />
      }
      stripedRows
      sortField="title"
      sortOrder={1}
      removableSort
      dataKey="id"
    >
      <Column
        header="Imagen"
        body={imageBodyTemplate}
        style={{ width: "5rem" }}
        exportable={false}
      />
      <Column
        field="title"
        header="Título"
        body={titleBodyTemplate}
        sortable
        style={{ minWidth: "14rem" }}
      />
      <Column
        header="Descuento"
        body={discountBodyTemplate}
        style={{ minWidth: "10rem" }}
      />
      <Column
        header="Vigencia"
        body={validityBodyTemplate}
        style={{ minWidth: "12rem" }}
      />
      <Column
        field="status"
        header="Estado"
        body={statusBodyTemplate}
        sortable
        style={{ minWidth: "8rem" }}
      />
      <Column
        header={<span className="row-actions-label">Acciones</span>}
        // Bloque contra el borde derecho con el mismo margen que la primera columna
          // a la izquierda; título e íconos centrados dentro del bloque.
        headerClassName="row-actions-th"
        bodyClassName="row-actions-cell"
        body={actionsBodyTemplate}
        style={{ minWidth: "8rem" }}
        exportable={false}
      />
    </DataTable>
  );
};
